import { ApiError } from "../../lib/api/core";

export type AuthFailureOperation = "login" | "register" | "resend" | "verify";

type AuthFailureDiagnostic = {
  operation: AuthFailureOperation;
  kind: "api" | "network" | "rejected_response" | "unexpected";
  status?: number;
  domainCode?: string;
  errorName?: string;
  validation?: Array<{ field: string; type: string }>;
};

const SAFE_TOKEN = /^[a-zA-Z0-9_.-]{1,80}$/;

function safeToken(value: unknown): string | undefined {
  return typeof value === "string" && SAFE_TOKEN.test(value) ? value : undefined;
}

function responseDetail(body: unknown): unknown {
  if (!body || typeof body !== "object") return undefined;
  return (body as { detail?: unknown }).detail;
}

function validationDiagnostics(detail: unknown): Array<{ field: string; type: string }> | undefined {
  if (!Array.isArray(detail)) return undefined;
  const diagnostics = detail.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const loc = Array.isArray((item as { loc?: unknown }).loc) ? (item as { loc: unknown[] }).loc : [];
    const field = [...loc].reverse().map(safeToken).find(Boolean) || "unknown";
    const type = safeToken((item as { type?: unknown }).type) || "unknown";
    return [{ field, type }];
  });
  return diagnostics.length ? diagnostics : undefined;
}

function domainCode(detail: unknown): string | undefined {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return undefined;
  return safeToken((detail as { code?: unknown }).code);
}

/**
 * Keep auth diagnostics separate from user-facing error mapping. The payload is
 * deliberately allow-listed so credentials, codes, response text, and inputs
 * can never be written to the browser log.
 */
export function logAuthFailure(operation: AuthFailureOperation, error: unknown): void {
  let diagnostic: AuthFailureDiagnostic;

  if (error instanceof ApiError) {
    const detail = responseDetail(error.body);
    diagnostic = {
      operation,
      kind: "api",
      status: error.status,
      domainCode: domainCode(detail),
      validation: validationDiagnostics(detail),
    };
  } else if (error instanceof TypeError) {
    diagnostic = { operation, kind: "network", errorName: "TypeError" };
  } else if (typeof error === "string") {
    diagnostic = { operation, kind: "rejected_response" };
  } else {
    diagnostic = {
      operation,
      kind: "unexpected",
      errorName: error instanceof Error ? safeToken(error.name) : undefined,
    };
  }

  console.error("[auth] Request failed", diagnostic);
}
